<!-- Safe alternative: the portfolio vs the same deposits put into EDO bonds and a bank deposit. -->
<script lang="ts">
  import * as E from "../core/engine";
  import * as X from "../core/analysis";
  import { app, need } from "../lib/state.svelte";
  import { download, toCsv } from "../lib/actions";
  import { fmtDate, fmtNum, parseNum, rangeLabel } from "../lib/format";
  import { baseOption, compactMoney, lineSeries, palette, series, timeAxis, tipMoney, tooltipRows, valueAxis, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const r = $derived(app.range);
  const bp = $derived(app.market?.bondParams);
  // values below are computed only when bond parameters are present (derived values are lazy)
  const spread = $derived(app.ui.alt?.spread ?? -1);
  const st = $derived(E.periodStats(m, A, r.start, r.end));
  const s0 = $derived(st.s0);
  const e = $derived(st.e);
  const sim = $derived(X.safeAlternative(m, A, s0, e, need(bp), app.market?.nbpRef, { spread }));
  const net = $derived(!!app.tax);
  const port = (i: number) => (net ? A.V[i] - A.L[i] : A.V[i]);
  const edoV = $derived(net ? sim.edoNet : sim.edo);
  const n = $derived(sim.n);
  const pEnd = $derived(port(e));
  const eEnd = $derived(edoV[n - 1]);
  const dEnd = $derived(sim.dep[n - 1]);
  const diff = $derived(pEnd - eEnd);
  const refNow = $derived.by(() => { const ref = app.market?.nbpRef || []; return ref.length ? ref[ref.length - 1] : null; });
  const yrows = $derived.by(() => {
    const out: { d: string; k: number; p: number; edo: number; dep: number; inv: number }[] = [];
    for (let k = 0; k < n; k++) {
      const d = m.days[s0 + k];
      if (d.slice(5) === "12-31" || k === n - 1) out.push({ d, k, p: port(s0 + k), edo: edoV[k], dep: sim.dep[k], inv: A.invested[s0 + k] - A.invested[s0] + A.V[s0] });
    }
    return out.reverse();
  });
  const days = $derived(m.days.slice(s0, e + 1));

  // after a change the field is re-rendered (as after redrawing the view): a fresh element with the normalised value
  let gen = $state(0);
  function setSpread(ev: Event & { currentTarget: HTMLInputElement }) {
    const el = ev.currentTarget;
    app.ui.alt = { ...(app.ui.alt ?? { spread: -1 }), spread: parseNum(el.value, -1) };
    gen++;
  }
  function exportCsv() {
    const rows = days.map((d, k) => ({ d, inv: A.invested[s0 + k] - A.invested[s0] + A.V[s0], p: port(s0 + k), edo: edoV[k], dep: sim.dep[k] }));
    download("porownanie_edo_lokata.csv", toCsv(rows, [{ k: "d", label: "data" }, { label: "wplacony_kapital", v: (x) => x.inv.toFixed(2) }, { label: "portfel", v: (x) => x.p.toFixed(2) }, { label: "edo", v: (x) => x.edo.toFixed(2) }, { label: "lokata", v: (x) => x.dep.toFixed(2) }]));
  }

  const altOpt = $derived.by(() => {
    const p = palette();
    return {
      ...baseOption(p), xAxis: timeAxis(p), yAxis: valueAxis(compactMoney, p, { scale: true }),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => tipMoney(v)) },
      series: [
        lineSeries(net ? "Portfel po podatku" : "Twój portfel", days.map((d, k) => [d, +port(s0 + k).toFixed(2)]), series(0)),
        lineSeries(net ? "EDO po podatku" : "EDO", days.map((d, k) => [d, +edoV[k].toFixed(2)]), series(1)),
        lineSeries("Lokata", days.map((d, k) => [d, +sim.dep[k].toFixed(2)]), series(2)),
        lineSeries("Wpłacony kapitał", days.map((d, k) => [d, A.invested[s0 + k] - A.invested[s0] + A.V[s0]]), p.muted, { step: "end" }),
      ],
    };
  });
  const diffOpt = $derived.by(() => {
    const p = palette();
    return {
      ...baseOption(p), legend: { show: false }, xAxis: timeAxis(p), yAxis: valueAxis(compactMoney, p),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => `${v >= 0 ? "+" : "−"}${tipMoney(Math.abs(v))}`) },
      series: [
        lineSeries("Portfel lepszy", days.map((d, k) => [d, +Math.max(0, port(s0 + k) - edoV[k]).toFixed(2)]), p.gain, { areaStyle: { color: p.gain, opacity: 0.15 }, lineStyle: { width: 1.5, color: p.gain } }),
        lineSeries("EDO lepsze", days.map((d, k) => [d, +Math.min(0, port(s0 + k) - edoV[k]).toFixed(2)]), p.loss, { areaStyle: { color: p.loss, opacity: 0.15 }, lineStyle: { width: 1.5, color: p.loss } }),
      ],
    };
  });
</script>

{#if !bp}
  <ViewHead title="Bezpieczna alternatywa" />
  <div class="notice">Brak parametrów obligacji — kliknij ↻, aby pobrać notowania i dane publiczne.</div>
{:else}
  <ViewHead title="Bezpieczna alternatywa" sub="{rangeLabel(r)} · te same wpłaty i wypłaty{net ? ' · po podatku Belki' : ''}" />
  <div class="kpis">
    <Kpi label="Twój portfel">{#snippet value()}<Money v={pEnd} />{/snippet}{#snippet sub()}XIRR <Pct v={sim.irr(pEnd)} />/rok{/snippet}</Kpi>
    <Kpi label="Gdyby wszystko szło w EDO">{#snippet value()}<Money v={eEnd} />{/snippet}{#snippet sub()}XIRR <Pct v={sim.irr(eEnd)} />/rok{net ? " · po Belce przy wykupie" : " · przed podatkiem"}{/snippet}</Kpi>
    <Kpi label={diff >= 0 ? "Portfel lepszy od EDO o" : "EDO lepsze od portfela o"}>{#snippet value()}<Signed v={Math.abs(diff) * (diff >= 0 ? 1 : -1)} />{/snippet}{#snippet sub()}<Pct v={eEnd > 0 ? pEnd / eEnd - 1 : null} /> względem EDO{/snippet}</Kpi>
    <Kpi label="Gdyby wszystko szło na lokatę">{#snippet value()}<Money v={dEnd} />{/snippet}{#snippet sub()}XIRR <Pct v={sim.irr(dEnd)} />/rok · po Belce{/snippet}</Kpi>
    <Kpi label="Portfel vs lokata">{#snippet value()}<Signed v={pEnd - dEnd} />{/snippet}{#snippet sub()}<Pct v={dEnd > 0 ? pEnd / dEnd - 1 : null} />{/snippet}</Kpi>
  </div>
  <Card title="Wartość: portfel vs EDO vs lokata" hint="EDO: każda wpłata kupuje obligacje z emisji danego miesiąca (oprocentowanie i marża tej serii, potem inflacja + marża), wypłaty umarzają obligacje proporcjonalnie, bez opłat za wcześniejszy wykup{refNow ? ` · lokata: stopa referencyjna NBP (obecnie ${fmtNum(refNow[1], 2)}%) ${spread >= 0 ? '+' : '−'} ${fmtNum(Math.abs(spread), 2)} pp, kapitalizacja miesięczna, Belka od odsetek na rachunkach opodatkowanych` : ''}">
    {#snippet extra()}<div class="row small">Lokata: stopa ref. NBP {#key gen}<input type="number" class="num" id="altSpread" value={String(spread)} step="0.25" style="width:70px" onchange={setSpread} />{/key} pp</div>{/snippet}
    <Chart id="chAlt" class="tall" option={altOpt} />
  </Card>
  <Card title="Przewaga portfela nad EDO" hint="powyżej zera — portfel wart więcej niż ta sama kwota w EDO"><Chart id="chAltDiff" class="short" option={diffOpt} /></Card>
  <Card title="Na koniec roku">
    {#snippet extra()}<button class="btn" id="expAlt" onclick={exportCsv}>Eksport CSV</button>{/snippet}
    <div class="table-wrap"><table><thead><tr><th class="l">Dzień</th><th>Wpłacony kapitał</th><th>Portfel</th><th>EDO</th><th>Lokata</th><th>Portfel − EDO</th><th>Portfel − lokata</th></tr></thead><tbody>
      {#each yrows as y (y.k)}
        <tr><td class="l"><b>{fmtDate(y.d)}</b></td><td><Money v={y.inv} /></td><td><Money v={y.p} /></td><td><Money v={y.edo} /></td><td><Money v={y.dep} /></td><td><Signed v={y.p - y.edo} /></td><td><Signed v={y.p - y.dep} /></td></tr>
      {/each}
    </tbody></table></div>
  </Card>
  {#if sim.approx.length}<div class="notice small">Dla {sim.approx.length} mies. ({sim.approx.slice(0, 6).join(", ")}{sim.approx.length > 6 ? "…" : ""}) nie ma jeszcze parametrów serii EDO — przyjęto najbliższą znaną serię. Serwer pobierze je z obligacjeskarbowe.pl przy następnym odświeżeniu notowań (↻).</div>{/if}
{/if}
